import { useEffect, useState } from "react";

import type { ContactMergeRecognition } from "../types";

type ContactMergeImageInputProps = {
  recognition?: ContactMergeRecognition;
  recognitionPending: boolean;
  recognitionErrorMessage: string;
  onRecognize: (file: File) => Promise<unknown> | void;
  onClear: () => void;
};

const acceptedImageTypes = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
]);

/** 接收联系人截图粘贴或文件选择，并就地展示读取与识别状态。 */
export function ContactMergeImageInput({
  recognition,
  recognitionPending,
  recognitionErrorMessage,
  onRecognize,
  onClear,
}: ContactMergeImageInputProps) {
  const [selectedFile, setSelectedFile] = useState<File>();
  const [previewUrl, setPreviewUrl] = useState("");
  const [localErrorMessage, setLocalErrorMessage] = useState("");

  useEffect(() => {
    if (!selectedFile || typeof URL.createObjectURL !== "function") {
      setPreviewUrl("");
      return undefined;
    }
    const nextPreviewUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(nextPreviewUrl);
    return () => URL.revokeObjectURL(nextPreviewUrl);
  }, [selectedFile]);

  const recognizeFile = (file: File) => {
    if (!acceptedImageTypes.has(file.type)) {
      setLocalErrorMessage("只支持 PNG、JPG、JPEG 或 WEBP 图片。");
      return;
    }
    setSelectedFile(file);
    setLocalErrorMessage("");
    void Promise.resolve(onRecognize(file)).catch(() => undefined);
  };

  const clearImage = () => {
    setSelectedFile(undefined);
    setLocalErrorMessage("");
    onClear();
  };

  return (
    <div className="contact-merge-image-input">
      <div className="contact-merge-image-actions">
        <label className="contact-merge-file-button">
          选择截图
          <input
            aria-label="选择联系人截图"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(event) => {
              const file = event.currentTarget.files?.[0];
              event.currentTarget.value = "";
              if (file) recognizeFile(file);
            }}
          />
        </label>
        <span>也可以先点击下方区域，再按 Ctrl+V 粘贴截图。</span>
      </div>
      <div
        aria-label="联系人截图粘贴区域"
        className="contact-merge-paste-zone"
        tabIndex={0}
        onPaste={(event) => {
          event.preventDefault();
          const clipboardFiles = Array.from(event.clipboardData.files ?? []);
          const fileFromFiles = clipboardFiles.find((file) =>
            acceptedImageTypes.has(file.type),
          );
          const imageItem = Array.from(event.clipboardData.items ?? []).find((item) =>
            acceptedImageTypes.has(item.type),
          );
          const file = fileFromFiles ?? imageItem?.getAsFile() ?? undefined;
          if (file) {
            recognizeFile(file);
            return;
          }
          setLocalErrorMessage("剪贴板中没有可识别的图片，请复制图片本身后重试。");
        }}
      >
        {selectedFile ? (
          <div className="contact-merge-image-preview">
            {previewUrl ? (
              <img src={previewUrl} alt="待识别联系人截图预览" />
            ) : null}
            <div>
              <strong>已读取：{selectedFile.name}</strong>
              <span>{Math.max(1, Math.ceil(selectedFile.size / 1024))} KB</span>
            </div>
            <button type="button" onClick={clearImage}>移除图片</button>
          </div>
        ) : (
          <span>将联系人截图粘贴到这里，原图不会保存。</span>
        )}
      </div>
      {recognitionPending ? (
        <div className="contact-merge-image-status" role="status">
          图片已读取，正在调用视觉模型识别……
        </div>
      ) : null}
      {localErrorMessage || recognitionErrorMessage ? (
        <div className="contact-merge-image-error" role="alert">
          {localErrorMessage || recognitionErrorMessage}
        </div>
      ) : null}
      {recognition ? (
        <div className="contact-merge-recognition">
          <strong>识别线索：</strong>
          <span>学校：{recognition.schoolName || "未识别"}</span>
          <span>
            联系人姓名：{recognition.contactNames.join("、") || "未识别"}
          </span>
          <span>电话：{recognition.phones.join("、") || "未识别"}</span>
        </div>
      ) : null}
    </div>
  );
}
